import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  Award,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Star,
  Calculator,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../../../lib/supabase';
import { generateFinancialData } from '../../utils/dataGeneration';

// Import design system components
import {
  getSchoolColor,
  getCollegeName,
  formatCurrency as formatCurr,
  BUTTON_STYLES
} from '../../shared/designSystem';
import SchoolCard from '../../shared/SchoolCard';
import ComparisonTable from '../../shared/ComparisonTable';
import { parseMarkdownAdvantages, parseInlineMarkdown } from '../../shared/markdownParser';

const FinancialAnalysisTab = ({
  colleges = [],
  currentUser,
  portfolioLocked = false,
  studentProfile = {},
  onDataGenerated,
  onDeleteCollege
}) => {
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [financialAnnotations, setFinancialAnnotations] = useState(null);
  const [loadingCache, setLoadingCache] = useState(true);
  const [generatingForCollege, setGeneratingForCollege] = useState(null);
  const [expandedScholarships, setExpandedScholarships] = useState({});

  // Parse financial annotations with markdown parser
  const parsedFinancialInsights = useMemo(() => {
    if (!financialAnnotations || typeof financialAnnotations !== 'string') {
      return [];
    }
    return parseMarkdownAdvantages(financialAnnotations);
  }, [financialAnnotations]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Generate cache key for current college selection
  const getCacheKey = () => {
    return colleges.map(c => c.id).sort().join(',');
  };

  // Fetch cached annotations from database
  const fetchCachedAnnotations = async () => {
    if (!currentUser?.id || colleges.length === 0) {
      setLoadingCache(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('portfolio_analysis')
        .select('comparison_cache')
        .eq('user_id', currentUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching cached annotations:', error);
        setLoadingCache(false);
        return;
      }

      if (data?.comparison_cache?.financial) {
        const cachedFinancial = data.comparison_cache.financial;
        const currentCollegeIds = getCacheKey();

        // Check if cached data matches current college selection
        if (cachedFinancial.colleges === currentCollegeIds) {
          setFinancialAnnotations(cachedFinancial.annotations);
        }
      }
    } catch (error) {
      console.error('Error fetching cached annotations:', error);
    } finally {
      setLoadingCache(false);
    }
  };

  // Load cached data on mount and when colleges change
  useEffect(() => {
    fetchCachedAnnotations();
  }, [currentUser?.id, colleges]);

  // Calculate merit aid likelihood (1-5 stars)
  const calculateMeritAidLikelihood = (college) => {
    // This would use actual student profile data in production
    const hasHonors = college.global_colleges?.honors_programs;
    const meritAvailable = college.global_colleges?.merit_aid_available;

    if (!meritAvailable) return 1;
    if (college.category === 'safety') return 4.5;
    if (college.category === 'target') return 3.5;
    if (college.category === 'reach') return 2;
    return 2.5;
  };

  // Render likelihood stars
  const renderLikelihoodStars = (score) => {
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} className="w-4 h-4 fill-current text-amber-500" />
        ))}
        {hasHalfStar && (
          <Star className="w-4 h-4 text-amber-500" style={{ fillOpacity: 0.5 }} />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
        ))}
        <span className="text-xs text-gray-600 ml-1">({score.toFixed(1)})</span>
      </div>
    );
  };

  // Estimate net cost based on profile
  const estimateNetCost = (college) => {
    const totalCost = college.global_colleges?.total_cost || 0;
    const meetsNeed = college.global_colleges?.meets_full_need;
    const avgAid = college.global_colleges?.average_aid_package || 0;

    // If no total cost data, return N/A
    if (!totalCost || totalCost === 0) {
      return null;
    }

    // For schools that meet full need, estimate based on income brackets
    if (meetsNeed) {
      // Rough estimate for middle-upper income families
      // Would use actual EFC in production
      const estimatedEFC = Math.min(totalCost * 0.25, 50000);
      return Math.max(estimatedEFC, 5000);
    }

    // For other schools, subtract average aid but don't go below 60% of total cost
    const withAid = totalCost - avgAid;
    return Math.max(withAid, totalCost * 0.6);
  };

  // Check if college has financial data
  const hasFinancialData = (college) => {
    const gc = college.global_colleges;
    // Must have numeric values greater than 0 for both
    const hasCost = gc?.total_cost && typeof gc.total_cost === 'number' && gc.total_cost > 0;
    const hasAid = gc?.average_aid_package && typeof gc.average_aid_package === 'number' && gc.average_aid_package > 0;
    return hasCost && hasAid;
  };

  // Generate financial data for a college
  const handleGenerateFinancial = async (college) => {
    setGeneratingForCollege(college.id);
    try {
      await generateFinancialData(college, () => {
        if (onDataGenerated) onDataGenerated();
      });
    } finally {
      setGeneratingForCollege(null);
    }
  };

  // Generate financial data for ALL colleges at once
  const handleGenerateAllFinancialData = async () => {
    setGeneratingForCollege('all');
    const collegesNeedingData = colleges.filter(c => !hasFinancialData(c));

    if (collegesNeedingData.length === 0) {
      toast.success('All colleges already have financial data!');
      setGeneratingForCollege(null);
      return;
    }

    const toastId = toast.loading(`Generating financial data for ${collegesNeedingData.length} college${collegesNeedingData.length > 1 ? 's' : ''}...`);

    try {
      for (const college of collegesNeedingData) {
        await generateFinancialData(college);
      }
      toast.success('Financial data generated for all colleges!', { id: toastId });
      if (onDataGenerated) onDataGenerated();
    } catch (error) {
      toast.error('Failed to generate some financial data', { id: toastId });
    } finally {
      setGeneratingForCollege(null);
    }
  };

  // Auto-generate financial data when tab loads if missing
  useEffect(() => {
    const collegesNeedingData = colleges.filter(c => !hasFinancialData(c));

    if (collegesNeedingData.length > 0 && !generatingForCollege) {
      console.log(`🤖 Auto-generating financial data for ${collegesNeedingData.length} colleges`);
      handleGenerateAllFinancialData();
    }
  }, [colleges.length]); // Only run when colleges array changes

  // Generate financial strategic analysis
  const generateFinancialAnalysis = async () => {
    setGeneratingAnalysis(true);
    try {
      console.log('💰 [FinancialAnalysisTab] Generating financial analysis for user:', currentUser?.id);
      console.log('💰 [FinancialAnalysisTab] Colleges being sent:', colleges.map(c => ({
        journey_id: c.id,
        global_id: c.global_colleges?.id,
        name: c.global_colleges?.name || c.name
      })));

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-strategic-annotations',
          colleges: colleges.map(c => ({
            journey_id: c.id,  // college_journey_colleges.id (for database updates)
            id: c.global_colleges?.id,  // global_colleges.id (for reference)
            name: c.global_colleges?.name || c.name,
            total_cost: c.global_colleges?.total_cost,
            meets_full_need: c.global_colleges?.meets_full_need,
            merit_aid_available: c.global_colleges?.merit_aid_available,
            average_aid_package: c.global_colleges?.average_aid_package,
            ...c.global_colleges
          })),
          tabType: 'financial',
          studentProfile,
          userId: currentUser?.id
        })
      });

      if (!response.ok) throw new Error('Failed to generate analysis');

      const { annotations } = await response.json();
      console.log('✅ [FinancialAnalysisTab] Received annotations:', annotations);

      if (annotations) {
        setFinancialAnnotations(annotations);
        console.log('✅ [FinancialAnalysisTab] Set financialAnnotations');
        toast.success('Financial analysis generated successfully');
      } else {
        console.warn('⚠️ [FinancialAnalysisTab] Response missing annotations');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to generate financial analysis');
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  // Define financial metrics for ComparisonTable
  const financialMetrics = [
    {
      label: 'Total Cost (Year 1)',
      field: 'global_colleges.total_cost',
      format: 'currency',
      highlight: true
    },
    {
      label: 'Tuition',
      getValue: (college) => {
        const outOfState = college.global_colleges?.tuition_out_of_state;
        const inState = college.global_colleges?.tuition_in_state;
        if (!outOfState) return 'N/A';
        if (inState && inState !== outOfState) {
          return `${formatCurr(outOfState)} (In-State: ${formatCurr(inState)})`;
        }
        return formatCurr(outOfState);
      }
    },
    {
      label: 'Room & Board',
      field: 'global_colleges.room_board',
      format: 'currency'
    },
    {
      label: 'Merit Aid Available?',
      getValue: (college) => {
        const hasAid = college.global_colleges?.merit_aid_available;
        return hasAid ? 'Yes' : hasAid === false ? 'No' : 'N/A';
      }
    },
    {
      label: 'Meets 100% Need?',
      getValue: (college) => {
        const meetsNeed = college.global_colleges?.meets_full_need;
        const noLoan = college.global_colleges?.no_loan_policy;
        let text = meetsNeed ? 'Yes' : meetsNeed === false ? 'No' : 'N/A';
        if (noLoan) text += ' (No-Loan Policy)';
        return text;
      }
    },
    {
      label: 'Avg Need-Based Aid',
      field: 'global_colleges.average_aid_package',
      format: 'currency'
    },
    {
      label: 'Avg Merit Aid',
      field: 'global_colleges.avg_merit_aid',
      format: 'currency'
    },
    {
      label: '% Receiving Aid',
      getValue: (college) => {
        const percent = college.global_colleges?.percent_receiving_aid;
        return percent ? `${Math.round(percent * 100)}%` : 'N/A';
      }
    }
  ];

  // Create AI update handlers for each college
  const aiUpdateHandlers = {};
  colleges.forEach(college => {
    if (!hasFinancialData(college)) {
      aiUpdateHandlers[college.id] = {
        tooltip: 'Generate financial data with AI',
        onClick: () => handleGenerateFinancial(college),
        isLoading: generatingForCollege === college.id
      };
    }
  });

  // Check if any college is missing financial data
  const anyMissingData = colleges.some(c => !hasFinancialData(c));

  return (
    <div className="space-y-8">
      {/* Financial Overview Table */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Financial Overview</h2>
          {anyMissingData && (
            <button
              onClick={handleGenerateAllFinancialData}
              disabled={generatingForCollege === 'all'}
              className="px-4 py-2 bg-blue-500/90 text-white text-sm font-medium rounded-lg border-2 border-slate-900 hover:bg-blue-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
            >
              {generatingForCollege === 'all' ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating All Data...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate All Financial Data
                </>
              )}
            </button>
          )}
        </div>
        <ComparisonTable
          colleges={colleges}
          metrics={financialMetrics}
          aiUpdateHandlers={aiUpdateHandlers}
        />
      </div>

      {/* Merit Scholarship Details */}
      {colleges.some(c => c.global_colleges?.merit_scholarship_details) && (
        <div className="bg-violet-50 rounded-lg border-2 border-violet-600 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-violet-500 rounded-lg p-2">
              <Award className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xs font-semibold text-violet-900">Merit Scholarship Details</h3>
          </div>
          <div className="space-y-4">
            {colleges.map((college, idx) => {
              const details = college.global_colleges?.merit_scholarship_details;
              if (!details) return null;

              const colors = getSchoolColor(idx);
              const isExpanded = expandedScholarships[college.id];
              const shouldTruncate = details.length > 200;
              const displayText = shouldTruncate && !isExpanded
                ? details.slice(0, 200) + '...'
                : details;

              return (
                <div key={college.id} className="border-b border-violet-200 last:border-0 pb-4 last:pb-0">
                  <h4 className={`text-xs font-bold ${colors.text} mb-2`}>
                    {getCollegeName(college)}
                  </h4>
                  <p className="text-xs text-violet-800 leading-relaxed">
                    {displayText}
                  </p>
                  {shouldTruncate && (
                    <button
                      onClick={() => setExpandedScholarships(prev => ({
                        ...prev,
                        [college.id]: !prev[college.id]
                      }))}
                      className="mt-2 text-xs font-medium text-violet-600 hover:text-violet-700 flex items-center gap-1"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          Show More
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Your Financial Position */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-slate-900">Your Financial Position</h3>
          <button
            onClick={generateFinancialAnalysis}
            disabled={generatingAnalysis}
            className="px-4 py-2 bg-teal-500/90 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
          >
            {generatingAnalysis ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {colleges[0]?.financial_position ? 'Regenerating...' : 'Analyzing...'}
              </>
            ) : (
              <>
                {colleges[0]?.financial_position ? <RefreshCw className="w-4 h-4" /> : <Calculator className="w-4 h-4" />}
                {colleges[0]?.financial_position ? 'Regenerate' : 'Analyze Position'}
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {colleges.map((college, idx) => {
            const netCost = estimateNetCost(college);
            const totalCost = college.global_colleges?.total_cost || 0;
            const savings = netCost !== null ? totalCost - netCost : 0;
            const fourYearTotal = netCost !== null ? netCost * 4 : null;
            const position = college.financial_position && typeof college.financial_position === 'object'
              ? college.financial_position?.summary || null
              : college.financial_position;

            return (
              <SchoolCard
                key={college.id}
                college={college}
                index={idx}
                category={college.category}
                showCategory={true}
                aiUpdate={!hasFinancialData(college) ? {
                  tooltip: 'Generate financial data with AI',
                  onClick: () => handleGenerateFinancial(college),
                  isLoading: generatingForCollege === college.id
                } : null}
                onDelete={onDeleteCollege}
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-600">Estimated Net Cost</p>
                    <p className="text-xs font-bold text-slate-900">
                      {netCost === null ? 'Data Unavailable' : formatCurrency(netCost)}
                    </p>
                  </div>

                  {netCost !== null && (
                    <>
                      <div>
                        <p className="text-xs text-slate-600">Potential Savings</p>
                        <p className="text-xs font-medium text-green-700">
                          {formatCurrency(savings)} in savings
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-600">4-Year Projection</p>
                        <p className="text-xs font-semibold text-slate-800">
                          {formatCurrency(fourYearTotal)}
                        </p>
                      </div>
                    </>
                  )}

                  {position && (
                    <div className="pt-3 border-t border-slate-300">
                      <p className="text-xs text-slate-700 leading-relaxed">{position}</p>
                    </div>
                  )}
                </div>
              </SchoolCard>
            );
          })}
        </div>
      </div>

      {/* Financial Strategic Annotations */}
      {portfolioLocked && (
        <div>
          {loadingCache ? (
            <div className="flex justify-center mb-4">
              <div className="flex items-center gap-2 text-gray-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-xs">Loading cached analysis...</span>
              </div>
            </div>
          ) : (
            <>
              {!financialAnnotations && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={generateFinancialAnalysis}
                    disabled={generatingAnalysis}
                    className="px-4 py-2 bg-teal-500/90 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
                  >
                    {generatingAnalysis ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Generating Financial Insights...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate Financial Analysis
                      </>
                    )}
                  </button>
                </div>
              )}
              {financialAnnotations && (
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-2">
                      <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
                      <h3 className="text-xs font-semibold text-amber-900">Financial Strategic Annotations</h3>
                    </div>
                    <button
                      onClick={generateFinancialAnalysis}
                      disabled={generatingAnalysis}
                      className="px-3 py-1 bg-amber-700 text-white text-xs font-medium rounded-lg hover:bg-amber-800 disabled:bg-gray-400 transition-colors flex items-center gap-2"
                    >
                      {generatingAnalysis ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Regenerate
                        </>
                      )}
                    </button>
                  </div>
                  <div className="space-y-6">
                {parsedFinancialInsights.map((section, sectionIdx) => {
                  // Skip main title sections (they're redundant)
                  if (section.type === 'main-title') {
                    return null;
                  }

                  return (
                    <div key={sectionIdx} className="space-y-3">
                      {/* Section Title */}
                      {section.title && (
                        <h3 className="text-xs font-bold text-amber-900 border-b-2 border-amber-300 pb-2">
                          {section.title}
                        </h3>
                      )}

                      {/* Section Content */}
                      <div className="space-y-3">
                        {section.content.map((item, itemIdx) => {
                          if (item.type === 'subsection') {
                            return (
                              <div key={itemIdx} className="space-y-2">
                                <h4 className="font-semibold text-amber-900 text-base">
                                  {item.title}
                                </h4>
                                <div className="space-y-1.5 ml-4">
                                  {item.content.map((subItem, subIdx) => {
                                    if (subItem.type === 'bullet') {
                                      return (
                                        <div key={subIdx} className="flex items-start gap-2">
                                          <span className="text-amber-500 mt-1 flex-shrink-0">•</span>
                                          <span className="text-xs text-amber-800 leading-relaxed">
                                            {subItem.boldPrefix && (
                                              <strong className="font-semibold">{subItem.boldPrefix}: </strong>
                                            )}
                                            {subItem.mainText}
                                          </span>
                                        </div>
                                      );
                                    }
                                    if (subItem.type === 'paragraph') {
                                      return (
                                        <p key={subIdx} className="text-xs text-amber-800 leading-relaxed"
                                           dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(subItem.text) }}
                                        />
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              </div>
                            );
                          }

                          if (item.type === 'bullet') {
                            return (
                              <div key={itemIdx} className="flex items-start gap-2">
                                <span className="text-amber-500 mt-1 flex-shrink-0">•</span>
                                <span className="text-xs text-amber-800 leading-relaxed">
                                  {item.boldPrefix && (
                                    <strong className="font-semibold">{item.boldPrefix}: </strong>
                                  )}
                                  {item.mainText}
                                </span>
                              </div>
                            );
                          }

                          if (item.type === 'paragraph') {
                            return (
                              <p key={itemIdx} className="text-xs text-amber-800 leading-relaxed"
                                 dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item.text) }}
                              />
                            );
                          }

                          return null;
                        })}
                      </div>
                    </div>
                  );
                })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Financial Tips */}
      <div className="bg-blue-50 rounded-lg border-2 border-blue-400 p-6">
        <div className="flex items-start gap-3">
          <div className="bg-blue-500 rounded-lg p-2 flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="text-xs font-semibold text-blue-900 mb-3">Financial Planning Tips</h4>
            <ul className="text-xs text-blue-800 space-y-2 leading-relaxed">
              <li>• Net cost estimates are approximations - run each college's Net Price Calculator for accuracy</li>
              <li>• Apply for merit scholarships early - many have fall deadlines</li>
              <li>• Consider the total 4-year investment, not just first-year costs</li>
              <li>• Factor in travel costs, especially for out-of-state schools</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalysisTab;